import { useEffect, useState } from "react"

import {
  Button,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"
import isEqual from "fast-deep-equal"

import type { ApiCallConfig, SimulationWrapperConfig } from "../types"
import { getApiCallValidationIssues } from "../utils/apiCallValidation"
import { ApiWrapperTopLevelConfigForm } from "./ApiWrapperTopLevelConfigForm"

export function ApiSetupDialog({
  config,
  onApply,
  onCancel,
  scope,
  disabled = false,
  onDraftOpen,
}: {
  config: SimulationWrapperConfig
  scope: "test-case" | "test-set"
  onApply: (config: SimulationWrapperConfig) => void
  onCancel: () => void
  disabled?: boolean
  onDraftOpen?: () => () => void
}) {
  useEffect(() => onDraftOpen?.(), [onDraftOpen])
  const isBulk = scope === "test-set"
  const [draft, setDraft] = useState(config)
  const [showValidation, setShowValidation] = useState(false)
  const calls = draft.pre_simulation ?? []
  const firstCall = calls[0]
  const remainingCalls = calls.slice(1)
  const validationIssues = getApiCallValidationIssues(firstCall)
  const updateFirstCall = (call: ApiCallConfig) =>
    setDraft({
      ...draft,
      pre_simulation: [call, ...remainingCalls],
    })

  return (
    <DialogContent size="lg">
      <DialogHeader>
        <DialogTitle>
          {isBulk ? "API Setup for All Test Cases" : "Test Case API Setup"}
        </DialogTitle>
        <DialogDescription>
          {isBulk ? (
            <>
              This will{" "}
              <strong>
                overwrite API Setup for every test case in this test set
              </strong>
              , including custom values. Save the test set to persist the
              changes. Cases added later need their own API Setup.
            </>
          ) : (
            <>
              Run an API call before this test case. Apply your changes, then
              save the test set.
            </>
          )}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        {disabled && (
          <p role="status" className="mb-3 text-sm text-coval-text-muted">
            API Setup is temporarily read-only. Your draft is preserved.
          </p>
        )}
        <ApiWrapperTopLevelConfigForm
          config={firstCall}
          onChange={updateFirstCall}
          disabled={disabled}
          hideHeaderValues={disabled}
        />
        {showValidation && validationIssues.length > 0 && (
          <p role="alert" className="mt-2 text-sm text-coval-danger-text">
            {validationIssues.join(" ")}
          </p>
        )}
      </DialogBody>
      <DialogFooter>
        <Button
          type="button"
          variant="ghost"
          disabled={disabled}
          className="mr-auto"
          onClick={() => {
            setDraft({ pre_simulation: [] })
            setShowValidation(false)
          }}
        >
          Clear
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (!isBulk && isEqual(draft, config)) {
              onCancel()
              return
            }
            if (validationIssues.length > 0) {
              setShowValidation(true)
              return
            }
            onApply(draft)
          }}
        >
          {isBulk ? "Overwrite All Test Cases" : "Apply to Test Case"}
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
